import * as IkeApi from '@ikegps/core-api';
import type { CollectionJSONObject, ParseJSONObject } from '@ikegps/core-api';
import { Log } from '../log';
import { CollectionModel, FormModel, type IkeCollectionLoader, IkeModel } from '../model';
import { IkeRestBatchControl } from './batch';
import { IkeRestCacheControl } from './cache';
import { type IkeFetchable, IkeFetchableMany, IkeFetchableOne, IkeFetchableTransform } from './fetchable';
import { IkeRestObject } from './objects/base';
import { IkeObjectCollectionModel } from './objects/collection.object';
import { IkeObjectFormModel } from './objects/form.object';
import { IkeRestObjects } from './objects/object';
import { IkeRestQuery } from './query';
import { IkeRest } from './rest';
import { IkeRestDepartmentAttachment } from './rest.department.attachment';
import { IkeRestDepartmentImage } from './rest.department.image';
import { IkeRestUser } from './rest.user';

export class IkeRestDepartment implements IkeCollectionLoader {
	private batcher: IkeRestBatchControl;

	Objects: IkeRestObjects = IkeRestObjects;
	Attachment: IkeRestDepartmentAttachment = new IkeRestDepartmentAttachment(this);

	tags: IkeFetchableMany<IkeApi.TagJSONObject>;
	tokens: IkeFetchableMany<IkeApi.TokenJSONObject>;
	formSummaries: IkeFetchableMany<IkeApi.FormSummaryJSONObject>;
	archivedFormSummaries: IkeFetchableMany<IkeApi.FormSummaryJSONObject>;

	empty: IkeFetchableOne<null>;

	cache: IkeRestCacheControl;

	boundUser: IkeRestUser;
	departmentId: string;
	log: Log;

	Tag = {
		getById: (objectId: string) => this.getOne(this.Objects.Tag, 'uuid', objectId),
		getByName: this.bindAction<IkeApi.IkeActionTagGet>('tag.get'),
		getOrCreate: this.bindAction<IkeApi.IkeActionTagGetOrCreate>('tag.create'),
		Summary: {
			getById: (objectId: string) => this.getOne(this.Objects.TagSummary, 'objectId', objectId),
		},
	};

	Event = {
		create: this.bindAction<IkeApi.IkeActionEvent>('event.create'),
	};

	Calibration = {
		getById: (objectId: string) => this.getOne(this.Objects.Calibration, 'objectId', objectId),

		create: this.bindAction<IkeApi.IkeActionCalibrationGetOrCreate>('calibration.create'),
	};

	Capture = {
		getById: (objectId: string) => this.getOne(this.Objects.Capture, 'objectId', objectId),
		getByUuid: (uuid: string) => this.getOne(this.Objects.Capture, 'uuid', uuid),

		Image: new IkeRestDepartmentImage(this),
		data: this.bindAction<IkeApi.IkeActionCaptureImage>('capture.data'),
		create: this.bindAction<IkeApi.IkeActionCaptureCreate>('capture.create'),
		getMask: this.bindAction<IkeApi.IkeActionCollectionCaptureMaskGet>('collection.capture.mask.get'),
	};

	AnnotationSummary = {
		getByUuid: (uuid: string) => this.getOne(this.Objects.AnnotationSummary, 'uuid', uuid),
		getById: (objectId: string) => this.getOne(this.Objects.AnnotationSummary, 'collectionId', objectId),
		getByRevision: (revision: string) => this.getOne(this.Objects.AnnotationSummary, 'revision', revision),

		create: this.bindAction<IkeApi.IkeActionAnnotationSummaryCreate>('annotation.summary.create'),
	};

	CollectionSummary = {
		getByUuid: (uuid: string) => this.getOne(this.Objects.CollectionSummary, 'uuid', uuid),
		getById: (objectId: string) => this.getOne(this.Objects.CollectionSummary, 'collectionId', objectId),
		getByRevision: (revision: string) => this.getOne(this.Objects.CollectionSummary, 'revision', revision),

		create: this.bindAction<IkeApi.IkeActionCollectionSummaryRefresh>('collection.summary.create'),
	};

	Collection = {
		getById: (objectId: string) => this.getOne(this.Objects.Collection, 'objectId', objectId),
		getByUuid: (uuid: string) => this.getOne(this.Objects.Collection, 'uuid', uuid),
		getByRevision: (revision: string) => this.getOne(this.Objects.Collection, 'revision', revision),
		getModel: (revision: string) => this.getModel(revision),

		assign: this.bindAction<IkeApi.IkeActionCollectionAssign>('collection.assign'),
		Import: {
			csv: this.bindAction<IkeApi.IkeActionCollectionCSVImport>('collection.import.csv'),
		},
		exportImportableExcelTemplate: this.bindAction<IkeApi.IkeActionExportImportableExcelTemplate>(
			'export.importable.excel.template'
		),
		delete: this.bindAction<IkeApi.IkeActionCollectionDelete>('collection.delete'),
		duplicate: this.bindAction<IkeApi.IkeActionCollectionDuplicate>('collection.duplicate'),
		moveToJob: this.bindAction<IkeApi.IkeActionCollectionMoveToJob>('collection.move.to.job'),
		moveToDepartment: this.bindAction<IkeApi.IkeActionCollectionMoveToDepartment>('collection.move.to.department'),
		create: this.bindAction<IkeApi.IkeActionCollectionGetOrCreate>('collection.create'),
		search: this.bindAction<IkeApi.IkeActionSearch>('collection.search'),
		repair: this.bindAction<IkeApi.IkeActionCollectionRepair>('collection.repair'),
		revert: this.bindAction<IkeApi.IkeActionCollectionRevert>('collection.revert'),
		insightProcess: this.bindAction<IkeApi.IkeActionDepartmentInsightProcess>('department.insight.process'),
		migrate: this.bindAction<IkeApi.IkeActionCollectionMigrate>('collection.migrate'),
	};

	Department = {
		getById: this.bindAction<IkeApi.IkeActionDepartmentGetDepartmentId>('department.get.departmentid'),

		User: {
			getProfileById: (objectId: string) => this.getOne(this.Objects.UserProfile, 'objectId', objectId),
			role: this.bindAction<IkeApi.IkeActionDepartmentUserRole>('department.user.role'),
			list: this.bindAction<IkeApi.IkeActionDepartmentUserList>('department.user.list'),
			profile: this.bindAction<IkeApi.IkeActionUserProfile>('user.profile'),
			isLicensed: this.bindAction<IkeApi.IkeActionUserIsLicensed>('user.is.licensed'),
		},
		Invite: {
			do: this.bindAction<IkeApi.IkeActionDepartmentInvite>('department.invite'),
		},
	};

	Task = {
		create: this.bindAction<IkeApi.IkeActionTaskCreate>('task.create'),
		status: this.bindAction<IkeApi.IkeActionTaskStatus>('task.status'),
	};

	Form = {
		getById: (id: string) => this.getOne(this.Objects.Form, 'objectId', id),
		getByRevision: (revision: string) => this.getOne(this.Objects.Form, 'version', revision),
		getModel: (revision: string) => this.form('version', revision),

		delete: this.bindAction<IkeApi.IkeActionFormDelete>('form.delete'),
		restore: this.bindAction<IkeApi.IkeActionFormRestore>('form.restore'),
		share: this.bindAction<IkeApi.IkeActionFormShare>('form.share'),
		copy: this.bindAction<IkeApi.IkeActionFormCopy>('form.copy'),

		Pla: {
			getRefreshPermission: this.bindAction<IkeApi.RefreshIPFFormLicenseCheck>('refresh.form.ipf.license.check'),
			metadata: this.bindAction<IkeApi.IkeActionPLAMetadata>('form.pla.metadata'),
			import: this.bindAction<IkeApi.IkeActionPLAImport>('form.pla.import'),
			importPoleforeman: this.bindAction<IkeApi.IkeActionPLAImportPoleforeman>('form.pla.import.poleforeman'),
			savePoleForemanDb: this.bindAction<IkeApi.IkeActionPLAImportSavePoleForemanDB>(
				'form.pla.import.save.poleforeman'
			),
		},
		Summary: {
			list: this.bindAction<IkeApi.IkeActionFormSummary>('form.summary'),
			getByRevision: (revision: string) => this.getOne(this.Objects.FormSummary, 'revision', revision),
		},
	};

	constructor(boundUser: IkeRestUser, departmentId: string) {
		this.boundUser = boundUser;
		this.departmentId = departmentId;
		this.log = boundUser.log.child({ departmentId });

		this.tokens = new IkeFetchableMany(this.log, IkeRest.createStore(), () => this.query(IkeRestObjects.Token).find());

		this.tags = new IkeFetchableMany(this.log, IkeRest.createStore(), () => this.query(IkeRestObjects.Tag).findAll());

		this.formSummaries = new IkeFetchableMany(this.log, IkeRest.createStore(), async () => {
			const response = await this.action<IkeApi.IkeActionFormSummary>('form.summary');
			return response.results;
		});

		this.archivedFormSummaries = new IkeFetchableMany(this.log, IkeRest.createStore(), async () => {
			const response = await this.action<IkeApi.IkeActionFormSummary>('form.summary', {
				includeArchived: true,
			});
			return response.results.filter((f) => f.isDeleted === true);
		});

		this.empty = new IkeFetchableOne(this.log, IkeRest.createStore<null>(), () => Promise.resolve(null));

		this.cache = new IkeRestCacheControl();
		this.batcher = new IkeRestBatchControl(this);
	}

	form<K extends keyof IkeApi.FormJSONObject & keyof FormModel>(key: K, value: string): IkeFetchable<FormModel> {
		let existing = this.cache.get(IkeObjectFormModel, key).get(key, value);
		if (existing == null) {
			existing = new IkeFetchableTransform<IkeApi.FormJSONObject, FormModel>(
				this.log,
				IkeRest.createStore(),
				this.getOne(IkeRestObjects.Form, key, value),
				async (formJSON: IkeApi.FormJSONObject): Promise<FormModel> => {
					if (formJSON == null) {
						throw new Error(`Unable to fetch form model with ${String(key)}: ${value}`);
					}
					return IkeModel.Form.read(formJSON);
				}
			);
			this.cache.get(IkeObjectFormModel, key).set(key, value, existing);
		}
		return existing;
	}

	private getModel(revision: string): IkeFetchable<CollectionModel> {
		const existing = this.cache.get(IkeObjectCollectionModel, 'revision').get('revision', revision);
		if (existing != null) {
			return existing;
		}
		const fetchable = new IkeFetchableTransform<CollectionJSONObject, CollectionModel>(
			this.log,
			IkeRest.createStore(),
			this.getOne(IkeRestObjects.Collection, 'revision', revision),
			async (c: CollectionJSONObject) => {
				if (c == null) {
					let cs = await this.CollectionSummary.getByRevision(revision).fetch();
					if (cs == null) {
						throw new Error(`Unable to fetch collection model with revision: ${revision}`);
					}
					const repaired = await this.CollectionSummary.create({ uuid: cs.uuid, repair: true });
					if (repaired) {
						c = await this.getOne(IkeRestObjects.Collection, 'uuid', cs.uuid).fetch();
						if (c == null) {
							throw new Error(`Unable to fetch collection model with uuid: ${cs.uuid}`);
						}
					} else {
						throw new Error(`Unable to repair collection model with revision: ${revision}`);
					}
				}

				const formModel = await this.form('version', c.formversion).fetch();
				const model = IkeModel.Collection.read(c, formModel, this, this.log);
				// Revision may change if collection was repaired
				this.cache.get(IkeObjectCollectionModel, 'revision').set('revision', model.revision, fetchable);

				// TODO if we ever want a model to reference another collection model
				// We cannot wait for it to be ready here.
				await model.ready();
				return model;
			}
		);

		this.cache.get(IkeObjectCollectionModel, 'revision').set('revision', revision, fetchable);
		return fetchable;
	}

	action<T extends IkeApi.IkeAction>(path: T['action'], data?: T['request']): Promise<T['response']> {
		return this.boundUser.core._post(path, data, this.boundUser.accessToken, this.departmentId);
	}

	actionRaw<T extends IkeApi.IkeAction>(path: T['action'], data?: T['request']): Promise<Response> {
		return this.boundUser.core._raw(path, data, this.boundUser.accessToken, this.departmentId);
	}

	query<T extends ParseJSONObject>(obj: IkeRestObject<T>): IkeRestQuery<T> {
		return new IkeRestQuery(this.boundUser.core, obj, this.boundUser.accessToken, this.departmentId, this.log);
	}

	async save<T extends ParseJSONObject>(
		obj: IkeRestObject<T>,
		value: T,
		modifications?: IkeApi.Modifications,
		silent?: boolean
	): Promise<T> {
		const response = await this.action<IkeApi.IkeActionSave<T>>(`${obj.actionName}.save` as any, {
			value,
			...(modifications && { modifications }),
			silent,
		});
		return response.results;
	}

	/**
	 * Get data from the API, generally should be called via the helper functions
	 *  @see IkeDepartmentRest.Collection.getByRevision
	 */
	getOne<T extends ParseJSONObject, K extends keyof T>(obj: IkeRestObject<T>, key: K, value: string): IkeFetchable<T> {
		let existing = this.cache.get(obj, key).get(key, value);
		if (existing == null) {
			const requestObj = async () => {
				const result = await this.batcher.batch(obj, key, value as any);
				if (obj.updateCache != null) {
					obj.updateCache(key, result, this);
				}
				return result;
			};

			existing = new IkeFetchableOne(this.log, IkeRest.createStore<any>(), requestObj);
			this.cache.get(obj, key).set(key, value, existing);
		}
		return existing;
	}

	/** Create a helper function to run a action */
	private bindAction<T extends IkeApi.IkeAction>(path: T['action']): (data: T['request']) => Promise<T['response']> {
		return (data: T['request']) => this.boundUser.core._post(path, data, this.boundUser.accessToken, this.departmentId);
	}

	updateBoundUser(newBoundUser: IkeRestUser) {
		this.boundUser = newBoundUser;
	}
}
